import type { Locale } from "../i18n/translate";

/**
 * The signed-in user as far as the client needs to know it.
 */
export interface AuthUser {
  /** OIDC subject (`sub`). */
  readonly subject: string;
  /** Display name from the ID token, if present. */
  readonly displayName: string | null;
}

/** Listener for sign-in and sign-out. */
export type AuthUserListener = (user: AuthUser | null) => void;

/**
 * Port for authentication (OIDC authorization code flow with PKCE, spec 9.1).
 * The concrete implementation is chosen in the composition root.
 */
export interface AuthService {
  /**
   * Whether the sign-in page asks for the work e-mail address before redirecting (T-117, MT-15).
   * The native apps serve every customer; the address lets the identity provider find the
   * customer's organization and its single sign-on.
   */
  readonly emailFirst: boolean;

  /**
   * Returns the stored session, even if its access token has expired (offline use).
   *
   * @returns The user or `null` if nobody is signed in.
   */
  getUser(): Promise<AuthUser | null>;

  /**
   * Returns a valid access token and renews it with the refresh token if necessary.
   *
   * @returns The token, or `null` if nobody is signed in or the session ended.
   * @throws NetworkError if the token has to be renewed but the identity provider is unreachable.
   */
  getAccessToken(): Promise<string | null>;

  /**
   * Starts the login by redirecting to the identity provider. Does nothing while a sign-out is
   * running: removing the local session makes the route guard ask for a login, which must not
   * replace the navigation to the end session endpoint.
   *
   * @param returnTo - Application path to open after the login.
   * @param locale - Current language of the app, passed as `ui_locales` so the sign-in pages use
   *   it (F-87, ADR-0211).
   * @param loginHint - Work e-mail address entered on the sign-in page, passed as `login_hint`.
   */
  login(returnTo: string, locale: Locale, loginHint?: string): Promise<void>;

  /**
   * Signs in again at the identity provider although a session is active (`prompt=login`), so the
   * person enters password and, for operator staff, the second factor once more; an irreversible
   * step of the operator area needs such a recent sign-in (F-70, ADR-0195). The application returns
   * to the given path afterwards.
   *
   * @param returnTo - Application path to open after the sign-in.
   * @param locale - Current language of the app, passed as `ui_locales` (F-87, ADR-0211).
   */
  confirmSignIn(returnTo: string, locale: Locale): Promise<void>;

  /**
   * Completes the login after the redirect back from the identity provider.
   *
   * @param url - The callback URL including the authorization response.
   * @returns The application path to open.
   */
  completeLogin(url: string): Promise<string>;

  /**
   * Ends the local session and, if online, the session at the identity provider.
   */
  logout(): Promise<void>;

  /**
   * Subscribes to sign-in and sign-out.
   *
   * @param listener - Called with the new user or `null`.
   * @returns A function that removes the subscription.
   */
  subscribe(listener: AuthUserListener): () => void;
}

/**
 * Accepts only application-internal paths as return targets (prevents open redirects).
 *
 * @param candidate - The requested return path.
 * @returns The path, or `/` if it is not a safe internal path.
 */
export function sanitizeReturnPath(candidate: unknown): string {
  const isInternal =
    typeof candidate === "string" &&
    candidate.startsWith("/") &&
    !candidate.startsWith("//") &&
    !candidate.startsWith("/\\") &&
    !candidate.startsWith("/auth/");
  const path = isInternal ? candidate : "/";

  return path;
}

/**
 * Checks an entered work e-mail address before it is passed to the identity provider.
 *
 * @param candidate - The entered text.
 * @returns The trimmed address, or `null` if it does not look like an e-mail address.
 */
export function normalizeLoginHint(candidate: string): string | null {
  const trimmed = candidate.trim();
  const at = trimmed.indexOf("@");
  const isAddress =
    trimmed.length <= 254 &&
    at > 0 &&
    at === trimmed.lastIndexOf("@") &&
    trimmed.indexOf(".", at) > at + 1 &&
    !trimmed.endsWith(".") &&
    !/\s/u.test(trimmed);
  const hint = isAddress ? trimmed : null;

  return hint;
}
